import { Box, Button, IconButton, Typography } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { ERPJobFormValues } from '../../../../ERPJobValidationSchema';
import tid from '@/common/application/utils/tid';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useListSalesAgentsAutocomplete } from '@/raptorjob/application/adapters/useListSalesAgentsAutocomplete';
import FormikAutocomplete from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { FormikCheckbox } from '@/common/presentation/components/forms/fields/FormikCheckbox';
import { FormikTextField } from '@/common/presentation/components/forms/fields/FormikTextField';
import { useCreateJobOptionalFieldsPreferences } from '@/raptorjob/presentation/hooks/useCreateJobOptionalFieldsPreferences';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';
import { useCustomerCodPreSelection } from '@/raptorjob/application/adapters/useCustomerCodPreSelection';
import { useJobPOSettings } from '@/raptorjob/application/adapters/useJobPOSettings';

interface ERPSalesInformationSectionProps {
  salesAgentId?: number | null;
  salesAgent?: string | null;
  mode?: JobFormMode;
  onPORequiredChange?: (poRequired: boolean) => void;
}

export function ERPSalesInformationSection({
  salesAgentId,
  salesAgent,
  mode = JOB_FORM_MODES.CREATE,
  onPORequiredChange
}: ERPSalesInformationSectionProps = {}) {
  const { values, setFieldValue } = useFormikContext<ERPJobFormValues>();
  const { visibility: optionalFieldsVisibility, setFieldVisible } = useCreateJobOptionalFieldsPreferences();
  const [salesAgentSearchTerm, setSalesAgentSearchTerm] = useState('');
  // Tracks the last default PO number applied; prevents overwriting user edits when the
  // effect re-runs for reasons unrelated to a customer/job-site change.
  const lastAppliedDefaultRef = useRef<string | null>(null);
  // Stable ref for poNumber so the pre-populate effect can read it without adding it to
  // the dependency array (which would cause user edits to reset the field).
  const poNumberRef = useRef(values.poNumber);
  poNumberRef.current = values.poNumber;
  const [showPONumberField, setShowPONumberField] = useState(
    mode === JOB_FORM_MODES.CREATE ? optionalFieldsVisibility.showPONumber || Boolean(values.poNumber) : Boolean(values.poNumber)
  );
  const isNew = mode === JOB_FORM_MODES.CREATE;
  const hasSetDefaultSalesAgent = useRef(false);

  const { poRequired, defaultPoNumber, loading: poSettingsLoading } = useJobPOSettings(values.customerId, values.jobSiteId);
  const {
    options: salesAgentOptions,
    loading: salesAgentLoading,
    filterOptions: salesAgentFilterOptions,
    defaultSalesAgentOption
  } = useListSalesAgentsAutocomplete(salesAgentSearchTerm, 1000);

  // Fetch customer's COD pre-selection setting (only in create mode)
  const { isCodPreSelected, loading: codLoading } = useCustomerCodPreSelection();

  // Compute initial option for Sales Agent - updates when salesAgents load
  type SalesAgentOption = {
    value: number;
    label: string;
    email?: string | null;
    phone?: string | null;
  };
  const salesAgentInitialOption = useMemo<SalesAgentOption | undefined>(() => {
    if (!values.salesAgentId) return undefined;

    // Try to find in loaded salesAgentOptions
    if (salesAgentOptions.length > 0) {
      const foundSalesAgent = salesAgentOptions.find((sa) => Number(sa.value) === Number(values.salesAgentId));
      if (foundSalesAgent) {
        const option: SalesAgentOption = {
          value: Number(foundSalesAgent.value),
          label: foundSalesAgent.label ?? 'Unknown Sales Agent',
          email: foundSalesAgent.email ?? null,
          phone: foundSalesAgent.phone ?? null
        };
        return option;
      }
    }

    // Fallback: use salesAgent data from jobData if available and ID matches
    if (salesAgentId && salesAgentId === values.salesAgentId && salesAgent) {
      const option: SalesAgentOption = {
        value: salesAgentId,
        label: salesAgent
      };
      return option;
    }

    return undefined;
  }, [values.salesAgentId, salesAgentOptions, salesAgentId, salesAgent]);

  // Set default sales agent once when creating new job and default option is available.
  // Only run once so that when the user clears the field (X), we do not re-apply the default (RAPTOR-9216).
  useEffect(() => {
    if (
      isNew &&
      !hasSetDefaultSalesAgent.current &&
      defaultSalesAgentOption &&
      typeof defaultSalesAgentOption.value === 'number' &&
      !values.salesAgentId &&
      !salesAgentLoading
    ) {
      hasSetDefaultSalesAgent.current = true;
      setFieldValue('salesAgentId', defaultSalesAgentOption.value);
    }
  }, [isNew, defaultSalesAgentOption, values.salesAgentId, salesAgentLoading, setFieldValue]);

  // Set COD checkbox based on customer's default ERP terms (only in create mode)
  useEffect(() => {
    if (!isNew || codLoading) return;
    setFieldValue('cod', isCodPreSelected);
  }, [isNew, isCodPreSelected, codLoading, values.customerId, setFieldValue]);

  // Lift poRequired to parent for validation schema
  useEffect(() => {
    onPORequiredChange?.(poRequired);
  }, [poRequired, onPORequiredChange]);

  // Pre-populate poNumber from defaultPoNumber.
  // Uses a ref to track what was last applied so user edits are not overwritten
  // when the effect re-runs due to other dependency changes.
  useEffect(() => {
    if (!defaultPoNumber || poSettingsLoading) {
      // Reset tracking when the default is cleared (customer/job-site deselected)
      lastAppliedDefaultRef.current = null;
      return;
    }

    const alreadyApplied = lastAppliedDefaultRef.current === defaultPoNumber;

    if (isNew) {
      // Create mode: apply the default when it arrives or changes (new selection),
      // but do NOT overwrite if the user has already edited away from it.
      if (!alreadyApplied) {
        setFieldValue('poNumber', defaultPoNumber);
        lastAppliedDefaultRef.current = defaultPoNumber;
      }
      return;
    }

    // Edit mode: only pre-populate when the saved poNumber is empty.
    if (!poNumberRef.current && !alreadyApplied) {
      setFieldValue('poNumber', defaultPoNumber);
      lastAppliedDefaultRef.current = defaultPoNumber;
    }
  }, [defaultPoNumber, poSettingsLoading, isNew, setFieldValue]);

  // Auto-show PO field when default exists
  useEffect(() => {
    if (defaultPoNumber && !showPONumberField && !poRequired) {
      setShowPONumberField(true);
    }
  }, [defaultPoNumber, showPONumberField, poRequired]);

  // Track when salesAgents finish loading to force re-render
  const salesAgentsLoadedKey = useRef(0);
  useEffect(() => {
    if (!salesAgentLoading && salesAgentOptions.length > 0 && values.salesAgentId) {
      salesAgentsLoadedKey.current += 1;
    }
  }, [salesAgentLoading, salesAgentOptions.length, values.salesAgentId]);

  // Memoize sales agent options with fallback from jobData
  const salesAgentOptionsWithFallback = useMemo(() => {
    const mappedOptions = salesAgentOptions.map((option) => ({
      ...option,
      value: Number(option.value)
    }));

    // Add salesAgent from jobData if it's not already in the options list
    if (salesAgentId && salesAgentId > 0 && salesAgent) {
      const exists = mappedOptions.some((opt) => Number(opt.value) === salesAgentId);
      if (!exists) {
        mappedOptions.push({
          value: salesAgentId,
          label: salesAgent
        });
      }
    }

    return mappedOptions;
  }, [salesAgentOptions, salesAgentId, salesAgent]);

  return (
    <>
      <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: 2, fontWeight: 600 }}>
        Sales Information
      </Typography>
      <Box sx={{ display: 'flex', gap: '12px', flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ flex: 1 }}>
          <FormikAutocomplete<{ value: number; label: string; email?: string | null; phone?: string | null }, ERPJobFormValues>
            key={`sales-agent-${values.salesAgentId}-${salesAgentsLoadedKey.current}`}
            id="salesAgentId"
            required
            label="Sales Agent"
            {...tid('jobs:create:sales-agent-autocomplete')}
            options={salesAgentOptionsWithFallback}
            optionsloading={salesAgentLoading}
            filterOptions={(options, state) => {
              const filtered = salesAgentFilterOptions(options, state);
              return filtered.map((option) => ({
                ...option,
                value: Number(option.value)
              }));
            }}
            initialOption={salesAgentInitialOption}
            onInputChange={(_, value) => setSalesAgentSearchTerm(value)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => Number(option.value) === Number(value.value)}
            onChange={(selectedOption) => {
              // Ensure we always set the numeric ID, never the label string
              if (selectedOption === null) {
                setFieldValue('salesAgentId', null);
              } else if (selectedOption && typeof selectedOption === 'object' && 'value' in selectedOption) {
                // Valid option object with numeric value
                setFieldValue('salesAgentId', Number(selectedOption.value));
              }
            }}
            sx={{ width: '100%' }}
            {...tid('jobs:create:sales-agent-autocomplete')}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            mt: 1,
            display: 'grid',
            gap: '12px',
            width: '100%'
          }}
        >
          <FormikCheckbox<ERPJobFormValues> id="cod" label="COD (Cash on Delivery)" testId="jobs:create:cod-checkbox" />
        </Box>
      </Box>
      {!showPONumberField && !poRequired && (
        <Button
          type="button"
          variant="text"
          onClick={() => {
            setShowPONumberField(true);
            if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showPONumber', true);
          }}
          startIcon={<AddCircleOutlineIcon />}
          sx={{ mb: 2 }}
          {...tid('jobs:create:add-po-number-button')}
        >
          ADD PO NUMBER
        </Button>
      )}
      {(showPONumberField || poRequired) && (
        <Box
          sx={{
            mt: 1,
            gap: '12px',
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: '50%' } }}>
            <FormikTextField
              id="poNumber"
              label="PO Number"
              sx={{ flex: 1 }}
              {...tid('jobs:create:po-number-input')}
              required={poRequired}
            />
            {!poRequired && (
              <IconButton
                title="Remove PO Number"
                onClick={() => {
                  setShowPONumberField(false);
                  setFieldValue('poNumber', '');
                  if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showPONumber', false);
                }}
                sx={{ mb: '20px' }}
                {...tid('jobs:create:remove-po-number-button')}
              >
                <DeleteOutline color="error" />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}
